import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Text capitalization utilities
export function capitalizeFirst(text) {
  if (!text || typeof text !== 'string') return text;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function capitalizeWords(text) {
  if (!text || typeof text !== 'string') return text;
  return text.split(' ').map(word => 
    word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : word
  ).join(' ');
}

export function capitalizeSentence(text) {
  if (!text || typeof text !== 'string') return text;
  return text.split('.').map(sentence => 
    sentence.trim() ? sentence.trim().charAt(0).toUpperCase() + sentence.trim().slice(1) : sentence
  ).join('.');
}

// Video URL processing utilities
/**
 * Normalizes escaped JSON strings (e.g., https:\\/\\/youtu.be\\/... → https://youtu.be/...)
 * @param {string} urlString - The URL string that may contain escaped slashes
 * @returns {string|null} - Normalized URL or null if invalid
 */
export function normalizeUrl(urlString) {
  if (!urlString || typeof urlString !== 'string') return null;
  // Replace escaped slashes with normal slashes
  return urlString.replace(/\\\//g, '/').trim();
}

/**
 * Extracts YouTube video ID from various URL formats
 * Supports: youtu.be/ID, youtube.com/watch?v=ID, youtube.com/embed/ID, youtube.com/shorts/ID
 * @param {string} url - The YouTube URL
 * @returns {string|null} - The 11-character YouTube video ID or null if not found
 */
export function extractYouTubeId(url) {
  if (!url) return null;
  
  const normalizedUrl = normalizeUrl(url);
  if (!normalizedUrl) return null;

  // YouTube URL patterns:
  // - https://youtu.be/VIDEO_ID
  // - https://www.youtube.com/watch?v=VIDEO_ID
  // - https://www.youtube.com/embed/VIDEO_ID
  // - https://www.youtube.com/shorts/VIDEO_ID
  // - https://youtube.com/... (variations)
  
  const patterns = [
    /(?:youtu\.be\/|youtube\.com\/(?:embed\/|shorts\/|watch\?v=))([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]{11})/,
  ];

  for (const pattern of patterns) {
    const match = normalizedUrl.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }

  return null;
}

/**
 * Converts video URL to embeddable format
 * - YouTube URLs → privacy-enhanced embed URL (youtube-nocookie.com)
 * - Direct video files (.mp4, .webm, etc.) → cleaned URL
 * - Other http/https URLs → cleaned URL for potential embedding
 * - Invalid URLs → null
 * @param {string} videoPath - The raw video URL from API
 * @returns {string|null} - Processed embeddable URL or null if invalid
 */
export function processVideoUrl(videoPath) {
  if (!videoPath || videoPath === '0' || videoPath === '') return null;
  
  // Normalize escaped JSON strings
  const normalizedPath = normalizeUrl(videoPath);
  if (!normalizedPath) return null;

  // Check if it's a YouTube URL
  const youtubeId = extractYouTubeId(normalizedPath);
  if (youtubeId) {
    // Return privacy-enhanced YouTube embed URL
    return `https://www.youtube-nocookie.com/embed/${youtubeId}?rel=0`;
  }

  // Check if it's a direct video file URL (http/https with video extension)
  const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'];
  const lowerPath = normalizedPath.toLowerCase();
  const isVideoFile = videoExtensions.some(ext => lowerPath.endsWith(ext));
  
  if (isVideoFile && (normalizedPath.startsWith('http://') || normalizedPath.startsWith('https://'))) {
    return normalizedPath;
  }

  // If it's an http/https URL but not a recognized format, return cleaned URL for potential direct embedding
  if (normalizedPath.startsWith('http://') || normalizedPath.startsWith('https://')) {
    return normalizedPath;
  }

  // Invalid or non-embeddable URL
  return null;
}

/**
 * Checks if a URL is a YouTube embed URL
 * @param {string} url - The URL to check
 * @returns {boolean} - True if the URL is a YouTube embed URL
 */
export function isYouTubeEmbed(url) {
  return url && url.includes('youtube-nocookie.com/embed/');
}

/**
 * Checks if a URL is a direct video file (ends with video extension)
 * @param {string} url - The URL to check
 * @returns {boolean} - True if the URL is a direct video file
 */
export function isDirectVideoFile(url) {
  if (!url) return false;
  const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'];
  return videoExtensions.some(ext => url.toLowerCase().endsWith(ext));
}